import { useQuery } from "@tanstack/react-query";
import { getAuthApiClient } from "@shared/apis/sidekick/api-factories";
import { extractQueryState } from "@shared/utils/extractQueryState";
import { cookieService } from "@shared/services/cookie-service";
import { apiResponseErrorHandler } from "@shared/apis/sidekick/api-error-handler";
import { ResponseError } from "@shared/apis/sidekick/generated";
import { isWrongBrandApiError } from "@shared/utils/is-wrong-brand-error";

const queryKey = "refresh-token";
const REFRESH_TIMEOUT_MS = 1000 * 15;
const ONE_MINUTE = 1000 * 60;
const FAST_RETRY_MS = 1000 * 2;

const isAccessTokenExpired = () => {
  const authTokenExp = cookieService.getCookie(
    "sidekick-authorization-token-exp",
  );
  return !!authTokenExp && Number(authTokenExp) < Date.now();
};

const isNonRetryableAuthError = (error: unknown) => {
  if (!(error instanceof Error) || error.name !== "ResponseError") return false;
  const status = (error as ResponseError).response.status;
  return status === 401 || status === 403;
};

type Params = {
  enabled: boolean;
  onSuccess: () => void;
  onSessionExpired?: () => void | Promise<void>;
  onWrongBrand?: () => void | Promise<void>;
  onUnexpectedError?: () => void;
};

export const useRefreshToken = (params: Params) => {
  const {
    enabled,
    onSuccess,
    onSessionExpired,
    onWrongBrand,
    onUnexpectedError,
  } = params;
  const api = getAuthApiClient();

  const query = useQuery({
    queryKey: [queryKey],
    queryFn: async () => {
      const authTokenExp = cookieService.getCookie(
        "sidekick-authorization-token-exp",
      );
      if (!authTokenExp) return false;
      const today = new Date().getTime();
      const THREE_HOURS = 1000 * 60 * 60 * 3;
      const isExpiring = Number(authTokenExp) - today < THREE_HOURS;

      if (!isExpiring) return false;

      try {
        const response = await api.authControllerRefresh({
          signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
        });
        onSuccess?.();
        return response;
      } catch (error) {
        const apiError = await apiResponseErrorHandler(error);
        if (apiError.status === 401) {
          await onSessionExpired?.();
        } else if (
          apiError.status === 403 &&
          isWrongBrandApiError(apiError.errors)
        ) {
          await onWrongBrand?.();
        } else {
          onUnexpectedError?.();
        }
        throw error;
      }
    },
    enabled,
    retry: (failureCount, error) =>
      isNonRetryableAuthError(error) ? false : failureCount < 1,
    refetchInterval: () =>
      isAccessTokenExpired() ? FAST_RETRY_MS : ONE_MINUTE,
    refetchIntervalInBackground: true,
  });

  return extractQueryState(query);
};
